import DOMPurify from "dompurify";

type StringMethodOptions = {
  preserveCase: boolean;
};

type ColorCallback = () => void;

type checkColorTypeProps = {
  color: string;
  callbackLight: ColorCallback;
  callbackDark: ColorCallback;
};

export const generateUniqueId = (): string => {
  const array = new Uint8Array(9);
  globalThis.window.crypto.getRandomValues(array);
  return `_${Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
};

export const stringSplitByCamelCase = (
  str: string,
  options?: StringMethodOptions,
): string => {
  const newStr = str.replaceAll(/([a-z])([A-Z])/g, "$1 $2");
  return options?.preserveCase ? newStr : newStr.toLowerCase();
};

export const stringSplitByHyphen = (
  str: string,
  options?: StringMethodOptions,
): string => {
  const newStr = str.replaceAll("-", " ");
  return options?.preserveCase ? newStr : newStr.toLowerCase();
};

export const validateName = (
  input: string,
): { isValid: boolean; error: string | null } => {
  const sanitizedInput = DOMPurify.sanitize(input);
  const regex = /^[a-zA-Z]+(?: [a-zA-Z]+)*$/;
  if (sanitizedInput !== input) {
    return { isValid: false, error: "HTML tags are not allowed." };
  }
  if (input.length < 2 || input.length > 25) {
    return { isValid: false, error: "Name must be between 2 and 25 characters." };
  }
  if (!regex.test(input)) {
    return { isValid: false, error: "Name must contain only letters and single spaces." };
  }
  return { isValid: true, error: null };
};

export const openEmailClient = (
  conciergeEmail: string,
  subject: string,
  body: string,
  salesEmail?: string,
) => {
  const cc = salesEmail ? `&cc=${encodeURIComponent(salesEmail)}` : "";
  globalThis.window.location.href = `mailto:${conciergeEmail}?subject=${encodeURIComponent(
    subject || "",
  )}&body=${encodeURIComponent(body.replaceAll(/\\n/gm, "\n") || "")}${cc}`;
};

export const openPhoneClient = (number: string) => {
  globalThis.window.location.href = `tel:${number}`;
};

export const checkColorType = ({
  color,
  callbackLight,
  callbackDark,
}: checkColorTypeProps): any => {
  const isHexColor = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(color);
  const isRgbColor = /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(color);

  if (!isHexColor && !isRgbColor) {
    return "#000000";
  }

  let r: number, g: number, b: number;

  if (isHexColor) {
    // Convert HEX to RGB
    const hexMatch = color.match(/[0-9a-fA-F]{2}/g);
    if (hexMatch) {
      r = Number.parseInt(hexMatch[0], 16);
      g = Number.parseInt(hexMatch[1], 16);
      b = Number.parseInt(hexMatch[2], 16);
    }
  } else {
    // Extract RGB values
    const rgbMatch = color.match(/\d+/g);
    if (rgbMatch) {
      r = Number.parseInt(rgbMatch[0], 10);
      g = Number.parseInt(rgbMatch[1], 10);
      b = Number.parseInt(rgbMatch[2], 10);
    }
  }

  // Check if color is light or dark
  const luminance = (0.299 * r! + 0.587 * g! + 0.114 * b!) / 255;
  const isColorLight = luminance > 0.6;

  // Execute the respective callback based on the color type
  if (isColorLight) {
    return callbackLight();
  }
  return callbackDark();
};

export const removeDuplicatesInArray = (array: string[]) => [...new Set(array)];

export const compareMaps = (
  mapA: Map<string, string | string[]>,
  mapB: Map<string, string | string[]>,
) => {
  const objA = Object.fromEntries(mapA);
  const objB = Object.fromEntries(mapB);

  return JSON.stringify(objA) === JSON.stringify(objB);
};

export const formatQAId = (id: string, label: string) => {
  if (!id) {
    return "";
  }

  if (!label) {
    return id;
  }

  return `${id}-${label.toLowerCase().split(" ").join("-")}`;
};

export const extractDealerSubdomainFromURL = () => {
  // Hostname example: sydneycitytoyota.dealer.toyota.com.au
  const parts = globalThis.window?.location.hostname.split(".");
  const dealerIndex = parts.indexOf("dealer");

  if (dealerIndex > 0) {
    const subdomain = parts[dealerIndex - 1];
    return subdomain || null;
  }

  return null;
};

export const removeTextDisclaimers = (text: string) =>
  // Match disclaimers within <sup></sup> tags and remove them
  text.replaceAll(/<sup[^>]*>.*?<\/sup>/gi, "").trim();

export const highlightWords = (
  text: string,
  variants: {
    blue?: string[];
    red?: string[];
  },
) => {
  if (!text) return text;

  const blueWords = variants.blue || [];
  const redWords = variants.red || [];

  const blueSet = new Set(blueWords.map((word) => word.toLowerCase()));
  const redSet = new Set(redWords.map((word) => word.toLowerCase()));

  const uniqueWords = new Set([...blueSet, ...redSet].flat());

  const regex = new RegExp(String.raw`\b(${[...uniqueWords].join("|")})\b`, "gi");
  const lineBreakTags = /<\/?br\s*\/?>/gi;

  return text
    .replace(regex, (word) => {
      const match = word.toLowerCase();
      const redMatch = [...redSet].find((phrase) => phrase === match);

      const grTextWithoutHyphen = /^GR(?!-)\w+$/i;
      const grTextWithSpace = /^GR\s\w+/i;

      // Highlight GR when followed another character that is not a hyphen (E.g "GR86")
      // This excludes any text that could represent a vehicle grade, such as "GR-Sport"
      if (redMatch && grTextWithoutHyphen.test(redMatch)) {
        const rest = word.slice(2);
        return `<span class="gr-text-highlight">GR</span>${rest}`;
      }

      // Highlight GR when followed by a space (E.g. "GR Corolla")
      if (redMatch && grTextWithSpace.test(redMatch)) {
        const rest = word.slice(2);
        return `<span class="gr-text-highlight">GR</span>${rest}`;
      }

      // Any word passed into the red variants
      if (redSet.has(match)) {
        return `<span class="gr-text-highlight">${word}</span>`;
      }

      // Any word passed into the blue variants.
      // The special <electrified> tag is managed by Toyota Kit's Typography component.
      if (blueSet.has(match)) {
        return `<electrified>${word}</electrified>`;
      }

      return word;
    })
    .replaceAll(lineBreakTags, "\n");
};

// Reference for easing functions: https://easings.net/
export const easeInOutQuad = (x: number): number =>
  x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

export const getParentElement = (el: HTMLElement, elParentLevel: number) => {
  let element: HTMLElement | null = el;
  const PARENT_LEVEL_LIMIT = 15;
  let currentLevel = 1;

  if (elParentLevel > PARENT_LEVEL_LIMIT) {
    return;
  }

  while ((element = element?.parentElement)) {
    if (currentLevel === elParentLevel) {
      return element;
    }
    currentLevel++;
  }
};

export const scrollToTop = (swiperElement: HTMLElement, parentLevel: number) => {
  getParentElement(swiperElement, parentLevel)?.scrollTo(0, 0);
};

export const setAttributes = (el: HTMLElement, attrs: Record<string, string>) => {
  for (const key in attrs) {
    el.setAttribute(key, attrs[key]);
  }
};

export const normaliseStrings = (str: string) =>
  str?.replaceAll(/[-\s]/g, "").toLowerCase();

export const openInNewTab = (link: string) =>
  globalThis?.window.open(link, "_blank", "noopener,noreferrer");

export const compareStrings = (a: string, b: string, items: string[]) => {
  if (!items || items.length === 0) {
    return 0;
  }

  const aIndex = items.indexOf(a);
  const bIndex = items.indexOf(b);

  const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
  const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

  return safeA - safeB;
};

export const isDesktopUserAgent = () => {
  const agent = globalThis?.window.navigator.userAgent ?? "";
  const isTouch = globalThis?.window.navigator.maxTouchPoints > 0;

  const isModernIpad = /Macintosh/.test(agent) && isTouch;
  const isAndroidTablet = /Android/i.test(agent) && !/Mobile/i.test(agent) && isTouch;

  return !/Mobi|iPhone|iPad|iPod/i.test(agent) && !isModernIpad && !isAndroidTablet;
};

export const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export const transposeMatrix = (matrix: any[][]): any[][] | undefined =>
  matrix?.[0]?.map((_, colIndex) => matrix.map((row) => row?.[colIndex]));

// transform 'dc,dcc' in     [ 'SR', 'dc,dcc', '4x4', 'AT', 'BEV' ]
// into an array of strings  [ 'SR', [ 'dc', 'dcc' ], '4x4', 'AT', 'BEV' ]
export const normaliseOptionSetId = (id: string): string | string[] =>
  id.includes(",") ? id.split(",").map((s) => s.trim()) : id;

export const isOptionInSelectedIds = (
  id: string | string[],
  selectedIds: string[],
): boolean => {
  if (Array.isArray(id)) {
    return id.some((option) => selectedIds.includes(option));
  }
  return selectedIds.includes(id);
};

export const sortItemsByWordCount = (items: string[]) => {
  const newItems = items;

  return newItems.sort((a: string, b: string) => {
    const wordCountA = a.split(" ").length;
    const wordCountB = b.split(" ").length;

    // alphabetical order
    if (wordCountA === wordCountB) {
      return a.localeCompare(b);
    }

    // word count order
    return wordCountA - wordCountB;
  });
};

export const convertKeysToSnakeCase = (obj: Record<string, any>) => {
  if (!obj || typeof obj !== "object") return obj;

  const result: Record<string, any> = {};

  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const snakeKey = key
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
        .toLowerCase();

      result[snakeKey] = obj[key];
    }
  }

  return result;
};
